import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { User, KeyRound, AtSign } from "lucide-react";
import { toast } from "sonner";

const inputCls = "bg-white border-[var(--ir-border)] rounded-none focus-visible:ring-[var(--ir-blue)]";
const Card = ({ title, icon: Icon, children }) => (
  <div className="bg-white border border-[var(--ir-border)] rounded-lg soft-shadow p-6">
    <h3 className="flex items-center gap-2 font-semibold text-[var(--ir-text)] mb-4"><Icon className="w-4 h-4 text-[var(--ir-orange)]" />{title}</h3>
    {children}
  </div>
);

export const AffiliateProfile = () => {
  const [p, setP] = useState({ name: "", email: "", paypal_email: "", mailing_address: "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [em, setEm] = useState({ new_email: "", current_password: "" });

  const load = () => api.get("/profile/me").then((r) => setP(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const savePayment = () => api.patch("/profile/me", { name: p.name, paypal_email: p.paypal_email, mailing_address: p.mailing_address })
    .then(() => toast.success("Profile saved")).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  const changePw = () => api.post("/profile/change-password", pw)
    .then((r) => { toast.success(r.data.message); setPw({ current_password: "", new_password: "" }); })
    .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  const changeEmail = () => api.post("/profile/change-email", em)
    .then((r) => { toast.success(r.data.message); setEm({ new_email: "", current_password: "" }); })
    .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));

  return (
    <div data-testid="affiliate-profile">
      <div className="flex items-center gap-2 mb-5">
        <User className="w-6 h-6 text-[var(--ir-orange)]" />
        <h2 className="font-display text-3xl text-[var(--ir-text)]">My Profile</h2>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <Card title="Account & payment details" icon={User}>
          <div className="space-y-3">
            <div><Label className="text-[var(--ir-muted)] text-xs mb-1 block">Name</Label><Input value={p.name || ""} onChange={(e) => setP({ ...p, name: e.target.value })} className={inputCls} data-testid="profile-name-input" /></div>
            <div><Label className="text-[var(--ir-muted)] text-xs mb-1 block">Current email</Label><Input value={p.email || ""} disabled className={`${inputCls} opacity-70`} data-testid="profile-email-display" /></div>
            <div><Label className="text-[var(--ir-muted)] text-xs mb-1 block">PayPal email (for payouts)</Label><Input value={p.paypal_email || ""} onChange={(e) => setP({ ...p, paypal_email: e.target.value })} placeholder="you@paypal.com" className={inputCls} data-testid="profile-paypal-input" /></div>
            <div><Label className="text-[var(--ir-muted)] text-xs mb-1 block">Mailing address</Label><Textarea value={p.mailing_address || ""} onChange={(e) => setP({ ...p, mailing_address: e.target.value })} rows={3} placeholder="Street, City, State, ZIP" className={inputCls} data-testid="profile-address-input" /></div>
            <Button onClick={savePayment} className="bg-[var(--ir-orange)] hover:bg-[var(--ir-orange-soft)] text-white rounded-none" data-testid="save-profile-btn">Save changes</Button>
          </div>
        </Card>
        <div className="space-y-5">
          <Card title="Change password" icon={KeyRound}>
            <div className="space-y-3">
              <div><Label className="text-[var(--ir-muted)] text-xs mb-1 block">Current password</Label><Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className={inputCls} data-testid="pw-current-input" /></div>
              <div><Label className="text-[var(--ir-muted)] text-xs mb-1 block">New password</Label><Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} placeholder="At least 6 characters" className={inputCls} data-testid="pw-new-input" /></div>
              <Button onClick={changePw} disabled={!pw.current_password || pw.new_password.length < 6} className="bg-[var(--ir-blue)] hover:opacity-90 text-white rounded-none disabled:opacity-50" data-testid="change-pw-btn">Update password</Button>
            </div>
          </Card>
          <Card title="Change email" icon={AtSign}>
            <div className="space-y-3">
              <p className="text-[var(--ir-muted)] text-xs">We'll send a confirmation link to the new address. Your current email stays active until you confirm.</p>
              <div><Label className="text-[var(--ir-muted)] text-xs mb-1 block">New email</Label><Input type="email" value={em.new_email} onChange={(e) => setEm({ ...em, new_email: e.target.value })} className={inputCls} data-testid="email-new-input" /></div>
              <div><Label className="text-[var(--ir-muted)] text-xs mb-1 block">Current password</Label><Input type="password" value={em.current_password} onChange={(e) => setEm({ ...em, current_password: e.target.value })} className={inputCls} data-testid="email-pw-input" /></div>
              <Button onClick={changeEmail} disabled={!em.new_email || !em.current_password} className="bg-[var(--ir-blue)] hover:opacity-90 text-white rounded-none disabled:opacity-50" data-testid="change-email-btn">Send confirmation link</Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};
